import React, {
  memo,
  ForwardedRef,
  useImperativeHandle,
  useRef,
  useMemo,
} from 'react';
import Chart from '..';
import { RadarChartChartProps, ChartRef } from '../_config/interface';

/**
 * RadarChartInner 组件用于渲染雷达图。
 * 
 * @param {Object} props - 组件的属性。
 * @param {string} [props.className=''] - 自定义的 CSS 类名。
 * @param {Object} [props.style={}] - 自定义的样式对象。
 * @param {number} [props.height] - 图表的高度。
 * @param {boolean} [props.hasDataZoom] - 是否启用数据缩放。
 * @param {Object} [props.chartEventConfig] - 图表事件配置。
 * @param {boolean} [props.notMerge] - 是否不合并选项。
 * @param {Object} restProps - 其他传递给图表的属性。
 * @param {Function} [restProps.setOtherConfigFunc] - 用于设置其他配置的函数。
 * @param {Function} [restProps.formatterFunc] - 自定义格式化函数。
 * @param {boolean} [restProps.isShowLegend=true] - 是否显示图例。
 * @param {Array} [restProps.legendData=[]] - 图例数据。
 * @param {Array} [restProps.indicator=[]] - 雷达图指标。
 * @param {Array} [restProps.series] - 图表系列数据。
 * 
 * @returns {JSX.Element} 渲染的雷达图组件。
 */
const RadarChartInner: React.ForwardRefRenderFunction<ChartRef, RadarChartChartProps> = (props, ref: ForwardedRef<ChartRef>) => {
  const {
    className = '',
    style = {},
    height,
    hasDataZoom,
    chartEventConfig,
    notMerge,
    ...restProps
  } = props;
  const chartRef = useRef<ChartRef>(null);

  const option = useMemo(() => {
    const { setOtherConfigFunc, formatterFunc, isShowLegend = true, legendData = [], indicator = [], series } = restProps || {};
    const option = {
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.75)',
        borderWidth: 0,
        borderRadius: 6,
        textStyle: {
          color: '#FFFFFF',
          fontSize: 12,
        },
        confine: true,
        hideDelay: 200,
        triggerOn: 'mousemove',
        padding: 0,
        formatter: formatterFunc ? formatterFunc : null
      },
      grid: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 40,
        containLabel: true
      },
      legend: {
        show: isShowLegend,
        type: 'scroll',
        data: legendData,
        selectedMode: true,
        orient: 'horizontal',
        itemHeight: 8,
        itemWidth: 8,
        icon: 'circle',
        bottom: -6,
        itemGap: 18,
        pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
        pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
        pageIconSize: 12, // 翻页按钮大小
        pageButtonPosition: 'end',// start
        left: 'center',
        textStyle: { // 图例文字的样式
          color: '#838697',
          fontSize: 12,
          lineHeight: 16,
        },
      },
      radar: {
        radius: '70%',
        indicator,
        triggerEvent: true,
        axisName: {
          color: "#262832",
          fontSize: "12px",
          fontWeight: 400,
        },
        tooltip: {
          formatter: formatterFunc ? formatterFunc : null
        },
      },
      series,
    };
    return setOtherConfigFunc ? setOtherConfigFunc?.(option) : option;
  }, [restProps]);

  // 刷新图表
  const refreshChart = () => {
    instance()?.setOption(option);
  };

  // 获取实例
  const instance = () => chartRef?.current?.instance();

  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    instance,
    refreshChart
  }));

  const chartStyle = {
    ...(height ? { height: `${height}px` } : {}),
    width: '100%',
    position: 'relative',
    ...style,
  };

  return (
    <div className={`chart-box ${className}`} style={chartStyle}>
      <Chart
        ref={chartRef}
        option={option}
        hasDataZoom={hasDataZoom}
        chartEventConfig={chartEventConfig}
        notMerge={notMerge}
      />
    </div>
  );
};

const RadarChart = memo(React.forwardRef(RadarChartInner));

export default RadarChart;
